/**
 * FeeClaimCard — shows claimable fee balance and executes claim transaction.
 *
 * Uses clanker-sdk availableRewards for on-chain balance read
 * and claimRewards for the claim transaction.
 * TransactionStateIndicator shows claim lifecycle.
 *
 * Story 6.3 — Task 4
 */

import { useEffect, useRef } from "react";

import { useAvailableRewards } from "../../hooks/chain/use-available-rewards";
import { useClaimRewards } from "../../hooks/chain/use-claim-rewards";
import { getBlockExplorerUrl } from "../../lib/wagmi-config";
import { TransactionStateIndicator } from "../shared/TransactionStateIndicator";
import { useToast } from "../ui/Toast";

type FeeClaimCardProps = {
	tokenId: string;
	tokenAddress: string;
	rewardRecipient: string;
};

export function FeeClaimCard({ tokenId, tokenAddress, rewardRecipient }: FeeClaimCardProps) {
	const {
		data: rewardsData,
		isLoading: rewardsLoading,
		error: rewardsError,
	} = useAvailableRewards({ tokenAddress, rewardRecipient });

	const { state, claim, reset } = useClaimRewards();
	const { addToast } = useToast();
	const prevStatusRef = useRef(state.status);

	// Show success toast when claim transitions to confirmed
	useEffect(() => {
		if (prevStatusRef.current !== "confirmed" && state.status === "confirmed") {
			addToast({ type: "success", title: "Fees claimed successfully!" });
		}
		prevStatusRef.current = state.status;
	}, [state.status, addToast]);

	const hasRewards = rewardsData?.amount !== undefined && rewardsData.amount > 0n;
	const isClaimInProgress =
		state.status !== "idle" &&
		state.status !== "confirmed" &&
		state.status !== "failed" &&
		state.status !== "rejected";

	function handleClaim() {
		// Errors are handled internally by useClaimRewards via setFailed/setRejected.
		// This catch only prevents unhandled promise rejection for unexpected edge cases.
		claim(tokenAddress, rewardRecipient, tokenId).catch((err: unknown) => {
			if (import.meta.env.DEV) {
				console.error("[FeeClaimCard] Unhandled claim error:", err);
			}
		});
	}

	function handleRetry() {
		reset();
	}

	// Show transaction state when claim is in progress or complete
	if (state.status !== "idle") {
		return (
			<div className="rounded-xl border border-pastel-border bg-white p-5">
				<h3 className="text-sm font-semibold text-pastel-black mb-3">Claim Fees</h3>
				<TransactionStateIndicator
					status={state.status}
					txHash={state.txHash}
					error={state.error}
					blockExplorerUrl={getBlockExplorerUrl()}
					onRetry={handleRetry}
				/>
				{state.status === "confirmed" && (
					<button
						type="button"
						onClick={reset}
						className="mt-3 w-full rounded-lg border border-pastel-border px-4 py-2 text-sm font-medium text-pastel-gray hover:bg-pastel-light-gray transition-colors"
					>
						Done
					</button>
				)}
			</div>
		);
	}

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5">
			<h3 className="text-sm font-semibold text-pastel-black mb-3">Claim Fees</h3>

			{/* Available rewards display */}
			<div className="mb-4">
				<p className="text-xs text-pastel-gray mb-1">Available to Claim</p>
				{rewardsLoading ? (
					<div className="h-7 w-32 bg-pastel-light-gray rounded animate-pulse" />
				) : rewardsError ? (
					<p className="text-sm text-red-500">Unable to read balance</p>
				) : (
					<p className="text-xl font-bold text-pastel-black">{rewardsData?.formatted ?? "0"}</p>
				)}
			</div>

			{/* Claim button */}
			<button
				type="button"
				onClick={handleClaim}
				disabled={!hasRewards || rewardsLoading || isClaimInProgress}
				className="w-full rounded-lg bg-pastel-vibrant-blue px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{!hasRewards && !rewardsLoading ? "No fees to claim" : "Claim Fees"}
			</button>
		</div>
	);
}
